/**
 * Variant Availability System for OpenCOTS Configurator
 * Provides pre-calculated availability feedback for heatframe configuration cards
 */
class ConfigCardVariantAvailability {
  constructor(configCard) {
    this.configCard = configCard;
    this.variants = [];
    this.fieldsets = [];
    this.currentSelections = {};
    
    this.initialize();
  }
  
  initialize() {
    try {
      // Parse variant JSON data
      const variantScript = this.configCard.querySelector('[data-variants-json]');
      if (!variantScript) {
        console.warn('No variant JSON found for config card');
        return;
      }
      
      this.variants = JSON.parse(variantScript.textContent);
      console.log(`Initialized variant availability for ${this.variants.length} variants`);
      
      // Find all fieldsets in order
      this.fieldsets = Array.from(this.configCard.querySelectorAll('fieldset.js.config-pill'));
      
      // Verify fieldset order mapping
      this.verifyFieldsetMapping();
      
      // Set up event listeners
      this.setupEventListeners();
      
      // Get initial selections
      this.updateCurrentSelections();

      // Initial availability calculation
      this.updateAvailability();

      // Auto-select single options
      this.ensureInitialSelections();

      // Initial Add to Kit button state
      this.updateAddToKitButton();
      
    } catch (error) {
      console.error('Error initializing variant availability:', error);
    }
  }
  
  verifyFieldsetMapping() {
    // Alert if fieldset count doesn't match expected option count
    if (this.fieldsets.length > 0 && this.variants.length > 0) {
      const firstVariant = this.variants[0];
      const optionCount = Object.keys(firstVariant).filter(key => key.startsWith('option')).length;
      
      if (this.fieldsets.length !== optionCount) {
        //alert(`VARIANT MAPPING ALERT: Found ${this.fieldsets.length} fieldsets but ${optionCount} options in variant data. Fieldset order may not match option1, option2, option3...`);
      }
    }
  }
  
  setupEventListeners() {
    // Listen for changes on all radio buttons in this config card
    this.fieldsets.forEach((fieldset, index) => {
      const radioButtons = fieldset.querySelectorAll('input[type="radio"]');
      radioButtons.forEach(radio => {
        radio.addEventListener('change', () => {
          this.clearCompatibilityHints(); // Clear any existing hints
          this.updateCurrentSelections();
          this.updateAvailability();
          this.updateAddToKitButton();
        });
        
        // Unified click handler for ALL buttons (available and disabled)
        radio.addEventListener('click', (event) => {
          if (radio.classList.contains('disabled')) {
            // For disabled buttons: show hints after the selection happens
            setTimeout(() => {
              this.showValidCombinationHints(radio.value, index);
            }, 0);
          } else {
            // For available buttons: normal selection behavior
            // Clear any existing hints since user made a valid selection
            setTimeout(() => {
              this.clearCompatibilityHints();
            }, 0);
          }
        });
      });
    });
    
    // Clear hints when clicking outside the config card
    document.addEventListener('click', (event) => {
      if (!this.configCard.contains(event.target)) {
        this.clearCompatibilityHints();
      }
    });
  }
  
  updateCurrentSelections() {
    this.currentSelections = {};
    
    this.fieldsets.forEach((fieldset, index) => {
      const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
      if (checkedRadio) {
        this.currentSelections[`option${index + 1}`] = checkedRadio.value;
      }
    });
  }
  
  updateAvailability(hintMode = null) {
    this.fieldsets.forEach((fieldset, fieldsetIndex) => {
      const optionKey = `option${fieldsetIndex + 1}`;
      const radioButtons = fieldset.querySelectorAll('input[type="radio"]');
      
      radioButtons.forEach(radio => {
        const isAvailable = this.isOptionAvailable(optionKey, radio.value, hintMode);
        
        // Apply/remove disabled class (don't use disabled attribute to allow clicks)
        if (isAvailable) {
          radio.classList.remove('disabled');
        } else {
          radio.classList.add('disabled');
          // Don't set disabled attribute - we want to allow clicks for hints
        }
      });
    });
  }
  
  isOptionAvailable(optionKey, optionValue, hintMode = null) {
    if (hintMode) {
      // In hint mode, only check if this option can work with the clicked disabled option
      // Ignore all other current selections
      const clickedOptionKey = hintMode.clickedOptionKey;
      const clickedOptionValue = hintMode.clickedOptionValue;

      // Task 185: Check if any variant has both the clicked option and this option AND has a GLB
      return this.variants.some(variant => {
        return variant[clickedOptionKey] === clickedOptionValue &&
               variant[optionKey] === optionValue &&
               variant.metafields?.custom?.glb_url;
      });
    } else {
      // Normal mode: Build filter criteria based on current selections
      const filterCriteria = { ...this.currentSelections };
      filterCriteria[optionKey] = optionValue;

      // Task 185: Check if any variant matches this combination AND has a GLB
      return this.variants.some(variant => {
        const matchesCriteria = Object.entries(filterCriteria).every(([key, value]) => {
          return variant[key] === value;
        });
        return matchesCriteria && variant.metafields?.custom?.glb_url;
      });
    }
  }
  
  getCurrentVariant() {
    // Find variant that exactly matches current selections
    return this.variants.find(variant => {
      return Object.entries(this.currentSelections).every(([key, value]) => {
        return variant[key] === value;
      });
    });
  }
  
  updateAddToKitButton() {
    const addToKitButton = this.configCard.querySelector('.log-variant-button');
    if (!addToKitButton) return;

    const helperNote = this.configCard.querySelector('.add-to-kit-helper-note');

    // Check if all options are selected
    const allOptionsSelected = this.fieldsets.every((fieldset, index) => {
      const optionKey = `option${index + 1}`;
      return this.currentSelections[optionKey] !== undefined;
    });

    if (!allOptionsSelected) {
      // Not all options selected
      addToKitButton.classList.add('disabled');
      addToKitButton.setAttribute('disabled', true);
      if (helperNote) helperNote.style.display = 'block';
      return;
    }

    // Task 185: Check if current combination has a GLB
    const currentVariant = this.getCurrentVariant();

    if (currentVariant && currentVariant.metafields?.custom?.glb_url) {
      // All options selected and variant has GLB
      addToKitButton.classList.remove('disabled');
      addToKitButton.removeAttribute('disabled');
      if (helperNote) helperNote.style.display = 'none';
    } else {
      // Invalid combination or no GLB available
      addToKitButton.classList.add('disabled');
      addToKitButton.setAttribute('disabled', true);
      if (helperNote) helperNote.style.display = 'block';
    }
  }

  ensureInitialSelections() {
    // Auto-select options when there's only one available choice
    this.fieldsets.forEach((fieldset, index) => {
      const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');

      // If no radio is checked, check if there's only one non-disabled option
      if (!checkedRadio) {
        const availableRadios = fieldset.querySelectorAll('input[type="radio"]:not([disabled])');

        if (availableRadios.length === 1) {
          // Only one option available - auto-select it
          const singleOption = availableRadios[0];
          singleOption.checked = true;

          // Update currentSelections to reflect this
          const optionName = singleOption.getAttribute('name');
          const optionValue = singleOption.value;
          this.currentSelections[`option${index + 1}`] = optionValue;

          console.log(`Auto-selected single option: ${optionName} = ${optionValue}`);
        }
      }
    });

    // After ensuring selections, update availability again
    if (this.fieldsets.some((fieldset, index) => {
      const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
      const optionKey = `option${index + 1}`;
      return checkedRadio && !this.currentSelections[optionKey];
    })) {
      this.updateAvailability();
    }
  }

  showValidCombinationHints(clickedValue, fieldsetIndex) {
    console.log(`Showing hints for ${clickedValue} at fieldset ${fieldsetIndex}`);
    
    const clickedFieldset = this.fieldsets[fieldsetIndex];
    const clickedRadio = clickedFieldset.querySelector(`input[value="${clickedValue}"]`);
    
    if (!clickedRadio) {
      console.error('Could not find clicked radio button');
      return;
    }
    
    // The button is already selected from the normal click behavior
    // We just need to set up hint mode to show compatible options
    
    // Create hint mode configuration
    const clickedOptionKey = `option${fieldsetIndex + 1}`;
    const hintMode = {
      clickedOptionKey: clickedOptionKey,
      clickedOptionValue: clickedValue
    };
    
    // Recalculate availability using hint mode logic
    // This evaluates each option independently against the clicked option
    this.updateAvailability(hintMode);
    
    // If this was a disabled option, keep it showing as disabled (selected + crossed out)
    if (clickedRadio.classList.contains('disabled')) {
      clickedRadio.classList.add('disabled');
    }
    
    console.log(`Hint mode: ${clickedOptionKey}="${clickedValue}"`);
  }
  
  clearCompatibilityHints() {
    // Return to normal availability calculation (no hint mode)
    this.updateCurrentSelections();
    this.updateAvailability(); // No hint mode parameter = normal mode
    this.updateAddToKitButton();
  }

  // Debug method to log current state
  debugState() {
    console.log('=== Variant Availability Debug ===');
    console.log('Current selections:', this.currentSelections);
    console.log('Current variant:', this.getCurrentVariant());
    console.log('Total variants:', this.variants.length);
    console.log('Fieldsets:', this.fieldsets.length);
  }
}

/**
 * Global manager for all config card variant availability instances
 */
class VariantAvailabilityManager {
  constructor() {
    this.instances = new Map();
    this.initialize();
  }
  
  initialize() {
    // Find all config cards with button picker type (heatframes)
    document.addEventListener('DOMContentLoaded', () => {
      this.initializeConfigCards();
    });
    
    // Also initialize when drawer content is dynamically loaded
    this.setupMutationObserver();
  }
  
  initializeConfigCards() {
    const configCards = document.querySelectorAll('.config-card[data-config-card]');
    
    configCards.forEach(card => {
      // Only initialize cards that have variant JSON (heatframes)
      const hasVariantJson = card.querySelector('[data-variants-json]');
      const hasButtonPicker = card.querySelector('fieldset.js.config-pill');
      
      if (hasVariantJson && hasButtonPicker && !this.instances.has(card)) {
        console.log('Initializing variant availability for config card');
        try {
          const instance = new ConfigCardVariantAvailability(card);
          this.instances.set(card, instance);
        } catch (error) {
          console.error('Error initializing variant availability for card:', error);
        }
      }
    });
    
    console.log(`Variant availability initialized for ${this.instances.size} config cards`);
  }
  
  setupMutationObserver() {
    // Watch for dynamically added content (drawer opening, section reloading)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if any added nodes contain config cards
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const newConfigCards = node.querySelectorAll?.('.config-card[data-config-card]') || [];
              if (newConfigCards.length > 0) {
                console.log(`Found ${newConfigCards.length} new config cards, initializing...`);
                setTimeout(() => this.initializeConfigCards(), 100);
              }
            }
          });
        }
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  
  // Public method to manually reinitialize (for testing)
  reinitialize() {
    this.instances.clear();
    this.initializeConfigCards();
  }
  
  // Debug method to check all instances
  debugAll() {
    console.log(`=== Variant Availability Manager Debug ===`);
    console.log(`Active instances: ${this.instances.size}`);
    this.instances.forEach((instance, card) => {
      console.log('Instance for card:', card);
      instance.debugState();
    });
  }
}

// Global instance
let variantAvailabilityManager = null;

// Initialize when script loads
if (typeof variantAvailabilityManager === 'undefined' || !variantAvailabilityManager) {
  variantAvailabilityManager = new VariantAvailabilityManager();
  
  // Expose debugging functions
  window.debugVariantAvailability = () => {
    if (variantAvailabilityManager) {
      variantAvailabilityManager.debugAll();
    }
  };
  
  window.reinitializeVariantAvailability = () => {
    if (variantAvailabilityManager) {
      variantAvailabilityManager.reinitialize();
    }
  };
  
  console.log('Variant Availability System loaded - Use debugVariantAvailability() or reinitializeVariantAvailability() in console');
}